import { useCallback, useMemo } from 'react';

import type { TimeFormat } from '@app/api/Branches/types';

import {
  CALENDAR_GRID_STEP_MINUTES_VALUES,
  CALENDAR_INTERVAL_HEIGHT_BY_SIZE,
  MAX_SELECTABLE_VISIBLE_COLUMN_COUNT,
  MIN_SELECTABLE_VISIBLE_COLUMN_COUNT,
} from '@pages/Calendar/data';
import type {
  AppointmentPreviewField,
  CalendarGridStepMinutes,
  CalendarIntervalHeightSize,
} from '@pages/Calendar/data';
import { useJournalTimeFormat } from '@pages/Calendar/hooks';
import { useIndividualCalendarContext } from '@pages/IndividualCalendar/context';

export const useIndividualCalendarHeaderSettings = () => {
  const {
    intervalHeightSize,
    gridStepMinutes,
    preferredMaxColumns,
    updateGridStepMinutes,
    updateIntervalHeightSize,
    updatePreferredMaxColumns,
    previewFields,
    updatePreviewFields,
    refreshCalendarData,
  } = useIndividualCalendarContext();

  const intervalHeightSizeOptions = useMemo(
    () =>
      (
        [
          ['small', 'Маленькая'],
          ['medium', 'Средняя'],
          ['large', 'Большая'],
        ] as const
      )
        .filter(([value]) => value in CALENDAR_INTERVAL_HEIGHT_BY_SIZE)
        .map(([value, label]) => ({
          label,
          value,
        })),
    []
  );

  const preferredMaxColumnOptions = useMemo(
    () =>
      Array.from(
        {
          length:
            MAX_SELECTABLE_VISIBLE_COLUMN_COUNT -
            MIN_SELECTABLE_VISIBLE_COLUMN_COUNT +
            1,
        },
        (_, index) => {
          const value = MIN_SELECTABLE_VISIBLE_COLUMN_COUNT + index;

          return {
            label: String(value),
            value,
          };
        }
      ),
    []
  );

  const {
    timeFormat,
    timeFormatOptions,
    isTimeFormatLoading,
    changeTimeFormatHandler: saveTimeFormat,
  } = useJournalTimeFormat();

  // Формат сетки приходит с загрузкой журнала (useIndividualCalendarData),
  // поэтому после сохранения журнал перезагружается — без этого подписи ждут
  // обновления страницы (issue/11215).
  const changeTimeFormatHandler = useCallback(
    async (value: TimeFormat) => {
      const isSaved = await saveTimeFormat(value);

      if (isSaved) {
        refreshCalendarData();
      }
    },
    [refreshCalendarData, saveTimeFormat]
  );

  const gridStepMinuteOptions = useMemo(
    () =>
      CALENDAR_GRID_STEP_MINUTES_VALUES.map((value) => ({
        label: value === 60 ? '1 час' : `${value} минут`,
        value,
      })),
    []
  );

  const intervalHeightSizeChangeHandler = useCallback(
    (value: CalendarIntervalHeightSize) => {
      updateIntervalHeightSize(value);
    },
    [updateIntervalHeightSize]
  );

  const preferredMaxColumnsChangeHandler = useCallback(
    (value: number) => {
      updatePreferredMaxColumns(value);
    },
    [updatePreferredMaxColumns]
  );

  const previewFieldsChangeHandler = useCallback(
    (value: AppointmentPreviewField[]) => {
      updatePreviewFields(value);
    },
    [updatePreviewFields]
  );

  const gridStepMinutesChangeHandler = useCallback(
    (value: CalendarGridStepMinutes) => {
      updateGridStepMinutes(value);
    },
    [updateGridStepMinutes]
  );

  return {
    intervalHeightSize,
    intervalHeightSizeOptions,
    gridStepMinutes,
    gridStepMinuteOptions,
    timeFormat,
    timeFormatOptions,
    isTimeFormatLoading,
    changeTimeFormatHandler,
    preferredMaxColumns,
    preferredMaxColumnOptions,
    gridStepMinutesChangeHandler,
    intervalHeightSizeChangeHandler,
    preferredMaxColumnsChangeHandler,
    previewFields,
    previewFieldsChangeHandler,
  };
};
